import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useErrorDialog } from "@/shared/components/ErrorDialog";
import { api, unwrap } from "@/shared/lib/apiClient";
import { splitsKeys } from "@/shared/lib/queryKeys";
import type { SplitsResponse } from "../splits.types";

export function useCreateSplit() {
  const queryClient = useQueryClient();
  const { showApiError } = useErrorDialog();
  return useMutation({
    mutationFn: async (name: string) =>
      unwrap(await api.POST("/api/splits", { body: { name } })),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: splitsKeys.all }),
    onError: (err) => showApiError(err, { title: "Kunde inte skapa splitten" }),
  });
}

export function useRenameSplit() {
  const queryClient = useQueryClient();
  const { showApiError } = useErrorDialog();
  return useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) =>
      unwrap(
        await api.PUT("/api/splits/{id}", {
          params: { path: { id } },
          body: { name },
        }),
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: splitsKeys.all }),
    onError: (err) =>
      showApiError(err, { title: "Kunde inte döpa om splitten" }),
  });
}

export function useDeleteSplit() {
  const queryClient = useQueryClient();
  const { showApiError } = useErrorDialog();
  return useMutation({
    mutationFn: async (id: string) =>
      unwrap(
        await api.DELETE("/api/splits/{id}", { params: { path: { id } } }),
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: splitsKeys.all }),
    onError: (err) =>
      showApiError(err, { title: "Kunde inte ta bort splitten" }),
  });
}

// Optimistic reorder: reindex sortOrder in the cache to the dragged order; rollback on error;
// settle → invalidate. Server persists the order in one transaction.
export function useReorderSplits() {
  const queryClient = useQueryClient();
  const { showApiError } = useErrorDialog();
  return useMutation({
    mutationFn: async (orderedIds: string[]) =>
      unwrap(await api.PUT("/api/splits/order", { body: { orderedIds } })),
    onMutate: async (orderedIds) => {
      await queryClient.cancelQueries({ queryKey: splitsKeys.all });
      const previous = queryClient.getQueryData<SplitsResponse>(splitsKeys.all);
      queryClient.setQueryData<SplitsResponse>(splitsKeys.all, (data) => {
        if (!data) return data;
        const byId = new Map(data.splits.map((s) => [s.id, s]));
        const splits = orderedIds.flatMap((id, i) => {
          const split = byId.get(id);
          return split ? [{ ...split, sortOrder: i }] : [];
        });
        return { ...data, splits };
      });
      return { previous };
    },
    onError: (err, _orderedIds, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(splitsKeys.all, ctx.previous);
      showApiError(err, { title: "Kunde inte ändra ordningen" });
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: splitsKeys.all }),
  });
}

// Optimistic: apply the whole batch of moves to the cache at once; rollback on error; settle → invalidate.
// One transaction server-side, so a mid-batch failure never persists a half-swap.
export function useSetAssignments() {
  const queryClient = useQueryClient();
  const { showApiError } = useErrorDialog();
  return useMutation({
    mutationFn: async (
      moves: { characterId: string; splitId: string | null }[],
    ) => unwrap(await api.PUT("/api/splits/assignments", { body: { moves } })),
    onMutate: async (moves) => {
      await queryClient.cancelQueries({ queryKey: splitsKeys.all });
      const previous = queryClient.getQueryData<SplitsResponse>(splitsKeys.all);
      queryClient.setQueryData<SplitsResponse>(splitsKeys.all, (data) => {
        if (!data) return data;
        const moved = new Set(moves.map((m) => m.characterId));
        const assignments = data.assignments.filter(
          (a) => !moved.has(a.characterId),
        );
        for (const m of moves) {
          if (m.splitId)
            assignments.push({
              characterId: m.characterId,
              splitId: m.splitId,
            });
        }
        return { ...data, assignments };
      });
      return { previous };
    },
    onError: (err, _moves, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(splitsKeys.all, ctx.previous);
      showApiError(err, { title: "Kunde inte flytta karaktären" });
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: splitsKeys.all }),
  });
}

// Optimistic: patch the character's note in the cache (empty removes it); rollback on error;
// settle → invalidate. Note follows the character; server upserts/deletes and nudges the board.
export function useSetSplitNote() {
  const queryClient = useQueryClient();
  const { showApiError } = useErrorDialog();
  return useMutation({
    mutationFn: async ({
      characterId,
      note,
    }: {
      characterId: string;
      note: string;
    }) =>
      unwrap(
        await api.PUT("/api/splits/notes", { body: { characterId, note } }),
      ),
    onMutate: async ({ characterId, note }) => {
      await queryClient.cancelQueries({ queryKey: splitsKeys.all });
      const previous = queryClient.getQueryData<SplitsResponse>(splitsKeys.all);
      queryClient.setQueryData<SplitsResponse>(splitsKeys.all, (data) => {
        if (!data) return data;
        const notes = data.notes.filter((n) => n.characterId !== characterId);
        if (note) notes.push({ characterId, note });
        return { ...data, notes };
      });
      return { previous };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(splitsKeys.all, ctx.previous);
      showApiError(err, { title: "Kunde inte spara anteckningen" });
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: splitsKeys.all }),
  });
}
